import React, { FC } from 'react';
import { formatDate } from 'utils/format';
import { Box } from '@mui/material';
import {
    TimelineConnector,
    TimelineContent,
    TimelineDot,
    TimelineItem,
    TimelineOppositeContent,
    TimelineSeparator,
} from '@mui/lab';
import ChatIcon from '@mui/icons-material/Chat';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import TaskIcon from '@mui/icons-material/Task';
import { FileDocumentEdit, Message } from 'mdi-material-ui';
import { OrderEvent, OrderEventType, OrderEventColor } from 'models/order-event';
import EventOrderCreated from 'components/shared/order-events-timeline/order-events/event-order-created.component';
import EventOrderStatus from 'components/shared/order-events-timeline/order-events/event-order-status.component';
import EventMessage from 'components/shared/order-events-timeline/order-events/event-message.component';
import EventFiles from 'components/shared/order-events-timeline/order-events/event-files.component';
import { eventOrganizationType } from 'components/shared/order-events-timeline/event-organization';

type OrderEventRecordProps = {
    item: OrderEvent;
    isMultipleOrders: boolean;
    last: boolean;
    onImageClick: (imageUrl: string) => void;
};
const OrderEventRecord: FC<OrderEventRecordProps> = ({
    item,
    isMultipleOrders,
    last,
    onImageClick,
}: OrderEventRecordProps) => {
    const renderRecordType = (type: OrderEventType) => {
        switch (type) {
            case OrderEventType.ORDER_CREATED:
                return <EventOrderCreated item={item} isMultipleOrders={isMultipleOrders} />;
            case OrderEventType.ORDER_MODIFIED:
            case OrderEventType.ORDER_RESEND:
            case OrderEventType.STATUS_CHANGE:
                return <EventOrderStatus item={item} displayOrderLabel={isMultipleOrders} />;
            case OrderEventType.NEW_COMMENT:
            case OrderEventType.NEW_CHAT_MESSAGE:
                return <EventMessage item={item} displayOrderLabel={isMultipleOrders} />;
            case OrderEventType.FILES_ADDED:
                return <EventFiles item={item} displayOrderLabel={isMultipleOrders} onImageClick={onImageClick} />;
            default:
                return undefined;
        }
    };

    const renderIcon = (type: OrderEventType) => {
        switch (type) {
            case OrderEventType.ORDER_CREATED:
                return <DescriptionIcon />;
            case OrderEventType.ORDER_MODIFIED:
            case OrderEventType.ORDER_RESEND:
                return <FileDocumentEdit />;
            case OrderEventType.STATUS_CHANGE:
                return <TaskIcon />;
            case OrderEventType.NEW_COMMENT:
                return <Message />;
            case OrderEventType.NEW_CHAT_MESSAGE:
                return <ChatIcon />;
            case OrderEventType.FILES_ADDED:
                return <AttachFileIcon />;
            default:
                return undefined;
        }
    };

    const getEventColor = (orderEvent: OrderEvent): string => {
        if (isMultipleOrders) {
            return getColor(orderEvent.color);
        }
        return eventOrganizationType(orderEvent) === 'lab' ? getColor('colorTwo') : getColor('colorOne');
    };

    const getColor = (color: 'colorOne' | 'colorTwo' | 'colorThree' | 'colorFour') => {
        switch (color) {
            case 'colorOne':
                return '#9e2b64';
            case 'colorTwo':
                return '#1f870c';
            case 'colorThree':
                return '#3e39bf';
            case 'colorFour':
                return '#3e39bf';
        }
    };

    return (
        <TimelineItem>
            <TimelineOppositeContent sx={{ flexGrow: 0, minWidth: 130 }}>
                <Box>
                    <strong>{formatDate(new Date(item.date), 'date')}</strong>
                </Box>
                <Box>{formatDate(new Date(item.date), 'time')}</Box>
            </TimelineOppositeContent>
            <TimelineSeparator>
                <TimelineDot sx={{ background: getEventColor(item) }}>{renderIcon(item.type)}</TimelineDot>
                {!last ? <TimelineConnector /> : undefined}
            </TimelineSeparator>
            <TimelineContent sx={{ paddingRight: 0 }}>
                <Box mt={-1} mb={1}>
                    {renderRecordType(item.type)}
                </Box>
            </TimelineContent>
        </TimelineItem>
    );
};

export default OrderEventRecord;
